import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Zap, Clock, Users, Award, Pause, Play, LogOut, CheckCircle, AlertTriangle,
} from 'lucide-react';
import axios from 'axios';
import { fmt12 } from '../utils/sportsUtils';
import { fmtDate } from '../utils/dates';
import MatchupCard, { fmtWait } from '../components/MatchupCard';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api` });

const STATUS_CONFIG = {
  registered: { color: '#737373', bg: 'rgba(115,115,115,0.08)', label: 'Registered', icon: Users },
  waitlisted: { color: '#A78BFA', bg: 'rgba(167,139,250,0.08)', label: 'On the waitlist — promoted automatically', icon: Users },
  active: { color: '#22C55E', bg: 'rgba(34,197,94,0.08)', label: 'In Queue', icon: Zap },
  paused: { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', label: 'Paused', icon: Pause },
  playing: { color: '#E63946', bg: 'rgba(230,57,70,0.08)', label: 'Playing', icon: Zap },
  done: { color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', label: 'Done for Today', icon: CheckCircle },
  flagged: { color: '#EF4444', bg: 'rgba(239,68,68,0.08)', label: 'Flagged — Check with Coordinator', icon: AlertTriangle },
  removing: { color: '#737373', bg: 'rgba(115,115,115,0.08)', label: 'Being Removed — Check with Coordinator', icon: Users },
};

export default function SportsOpenPlayPlayerPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionMsg, setActionMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/sports/open-play/player/${token}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh: every 8 s while live; every 30 s before Start so an early
  // check-in flips to the live pass by itself (R1-110).
  const sessionStatus = data?.session?.status;
  useEffect(() => {
    const every = sessionStatus === 'active' ? 8000
      : ['registration_open', 'registration_closed', 'draft'].includes(sessionStatus) ? 30000 : 0;
    if (!every) return;
    const iv = setInterval(() => { if (!document.hidden) fetchData(); }, every);
    return () => clearInterval(iv);
  }, [sessionStatus, fetchData]);

  const doAction = async (action) => {
    setActionLoading(true);
    try {
      const r = await api.put(`/sports/open-play/player/${token}/${action}`);
      setActionMsg(r.data?.message || '');
      await fetchData();
    } catch (e) {
      const d = e.response?.data?.detail;
      setActionMsg((d && typeof d === 'object' ? d.message : d) || `That didn't work — try again.`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A', color: '#737373' }}>
      <div className="text-center">
        <Zap className="h-8 w-8 mx-auto mb-3 animate-pulse" style={{ color: '#E63946' }} />
        <p>Loading your session...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }}>
      <div className="text-center">
        <AlertTriangle className="h-10 w-10 mx-auto mb-3" style={{ color: '#EF4444' }} />
        <p className="text-lg font-bold" style={{ color: '#fff' }}>{error}</p>
        <p className="text-sm mt-1" style={{ color: '#737373' }}>Check your link or contact the coordinator.</p>
      </div>
    </div>
  );

  const { player, session, division, queue_position, facility } = data;
  const config = STATUS_CONFIG[player.status] || STATUS_CONFIG.registered;
  const StatusIcon = config.icon;
  const match = data.upcoming_match;
  const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
  // Check In is offered only when the server would accept it (R1-110).
  const checkInOpen = ['registration_open', 'registration_closed', 'active'].includes(session.status)
    && (session.status === 'active' || !session.check_in_opens_at || new Date() >= new Date(session.check_in_opens_at));
  const canSelfCheckIn = ['registered', 'flagged'].includes(player.status)
    && session.check_in_mode !== 'coordinator' && checkInOpen;
  const checkInHint = ['registered', 'flagged'].includes(player.status) && session.status !== 'ended' && !canSelfCheckIn
    ? (session.check_in_mode === 'coordinator' ? 'Check in at the desk when you arrive.'
      : session.check_in_opens_at && ['registration_open', 'registration_closed'].includes(session.status)
        ? `Check-in opens at ${fmtTime(session.check_in_opens_at)} (an hour before the start).`
        : null)
    : null;
  const standingsPath = facility?.slug ? `/book/${facility.slug}/open-play/${session.id}` : null;

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      {/* Header */}
      <div className="px-4 py-6 text-center" style={{ background: '#111', borderBottom: '1px solid #1C1C1C' }}>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight uppercase"
          style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
          {session.name}
        </h1>
        {facility && (
          <p className="text-xs mt-1" style={{ color: '#555' }}>{facility.name}</p>
        )}
        <div className="flex justify-center gap-3 mt-2 text-xs" style={{ color: '#737373' }}>
          {session.date && <span>{fmtDate(session.date)}</span>}
          {session.time_start && <span>{fmt12(session.time_start)} – {fmt12(session.time_end)}</span>}
          {session.game_mode && <span className="uppercase">{session.game_mode}</span>}
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-5">
        {/* Player greeting + avatar */}
        <div className="text-center">
          <div className="flex flex-col items-center">
            {player.avatar_url ? (
              <img src={player.avatar_url} alt={player.name} data-testid="player-avatar"
                className="h-20 w-20 rounded-full object-cover mb-2"
                style={{ border: `3px solid ${config.color}` }} />
            ) : (
              <div className="h-20 w-20 rounded-full flex items-center justify-center text-2xl font-black mb-2"
                style={{ background: `${config.color}22`, color: config.color, border: `3px solid ${config.color}55`, fontFamily: 'Oswald, sans-serif' }}>
                {player.name?.[0]?.toUpperCase()}
              </div>
            )}
            <p className="text-sm" style={{ color: '#737373' }}>Hi</p>
            <h2 className="text-2xl font-bold tracking-tight"
              style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
              {player.name}
            </h2>
            <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
              {division && (
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: '#1C1C1C', color: '#A3A3A3' }}>
                  {division.name}
                </span>
              )}
              {player.org_label && (
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6' }}>
                  {player.org_label}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Status card */}
        <div className="rounded-2xl p-5 text-center" style={{ background: config.bg, border: `1px solid ${config.color}33` }}>
          <StatusIcon className="h-8 w-8 mx-auto mb-2" style={{ color: config.color }} />
          <p className="text-lg font-bold uppercase" style={{ color: config.color, fontFamily: 'Oswald, sans-serif' }}>
            {config.label}
          </p>

          {/* On the bench — same numbers the big screen shows (checklist 1.8/3.2) */}
          {data.eta_kind === 'bench' && queue_position && (
            <div className="mt-3">
              <p className="text-3xl font-bold" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
                #{queue_position}
              </p>
              <p className="text-xs" style={{ color: '#737373' }}>on the bench</p>
              {data.eta_minutes != null && data.joins_set_number && (
                <span className="inline-block text-xs font-bold px-3 py-1 rounded-full mt-2"
                  style={{ background: '#F59E0B14', color: '#F59E0B', border: '1px solid #F59E0B30' }}>
                  joins Set {data.joins_set_number} · est. {fmtWait(data.eta_minutes) || 'soon'}
                </span>
              )}
            </div>
          )}

          {/* Saved seat in a TBA game (owner 2026-10-05): partner and opponents
              are picked about one game before it plays. */}
          {player.status === 'active' && data.eta_kind === 'open' && data.open_game && session.status === 'active' && (
            <div data-testid="player-open-game" className="mt-3 rounded-xl px-3 py-3 text-center"
              style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid #F59E0B44' }}>
              <p className="text-xs font-bold uppercase" style={{ color: '#FCD34D', letterSpacing: '0.08em' }}>
                You're in game #{data.open_game.position}
              </p>
              <p className="text-2xl font-bold mt-1" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
                {data.open_game.eta_minutes ? fmtWait(data.open_game.eta_minutes) : 'Next free court'}
              </p>
              <div className="flex justify-center flex-wrap gap-1.5 mt-2">
                {(data.open_game.with || []).map(n => (
                  <span key={n} className="text-[11px] font-bold px-2 py-0.5 rounded-md"
                    style={{ border: '1.5px solid #22C55E88', color: '#4ADE80', background: '#22C55E10' }}>{n}</span>
                ))}
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-md"
                  style={{ border: '1.5px solid #3B82F688', color: '#93C5FD', background: '#3B82F610' }}>You</span>
                {Array.from({ length: data.open_game.open_seats || 0 }).map((_, k) => (
                  <span key={k} className="text-[11px] font-extrabold px-2 py-0.5 rounded-md"
                    style={{ border: '1.5px dashed #F59E0B88', color: '#FCD34D', background: '#F59E0B0A' }}>TBA</span>
                ))}
              </div>
              <p className="text-[13px] mt-2" style={{ color: '#DADADA' }}>
                Your partner and opponents are picked about one game before you play.
              </p>
              <p className="text-[11.5px] mt-1.5" style={{ color: '#8A8A8A' }}>
                Your spot is saved. The app picks the fairest mix from whoever is free: fewest games first,
                even teams, new partners.
              </p>
            </div>
          )}

          {/* Your next match — from the server's queue view: queued in the pool,
              called and waiting for a court (TBA), or on court now (R1-51, R1-108). */}
          {['active', 'playing'].includes(player.status) && match && session.status === 'active' && (
            <div data-testid="player-match-card" className="mt-3 rounded-xl overflow-hidden text-left"
              style={{ background: 'rgba(0,0,0,0.3)', border: `1px solid ${match.tba ? '#F59E0B33' : '#22C55E33'}` }}>
              <p className="text-xs font-bold uppercase px-3 pt-2.5 text-center" style={{ color: match.tba ? '#F59E0B' : '#22C55E' }}>
                {match.tba
                  ? `Called — you're on the next free court${match.eta_minutes ? ` · ${fmtWait(match.eta_minutes)}` : ''}`
                  : match.status === 'active'
                    ? `YOUR MATCH — GO!${match.court_name ? ` · ${match.court_name}` : ''}`
                    : data.match_queue_position
                      ? `Your match is #${data.match_queue_position} in the queue`
                      : 'Your next match'}
              </p>
              <MatchupCard variant="queued"
                teamA={match.team_a_players || []}
                teamB={match.team_b_players || []}
                namesA={[match.team_a]} namesB={[match.team_b]}
                etaMinutes={match.status === 'active' ? null : match.eta_minutes}
                highlight={match.my_side} />
            </div>
          )}

          {/* Paused info */}
          {player.status === 'paused' && player.paused_at && (
            <p className="text-xs mt-2" style={{ color: '#737373' }}>
              Paused since {new Date(player.paused_at).toLocaleTimeString()}. If your match is called
              while you're paused, someone takes your spot.
            </p>
          )}

          {/* Flagged message — what they can actually do (R1-110) */}
          {player.status === 'flagged' && (
            <p data-testid="player-flagged-msg" className="text-xs mt-2" style={{ color: '#EF4444' }}>
              You were marked absent from your last match.{' '}
              {canSelfCheckIn ? 'Tap Check In below when you\'re back'
                : 'Please check in with the coordinator to continue'}
              {data.auto_return_at ? ` — or you return to the queue automatically at ${fmtTime(data.auto_return_at)}.` : '.'}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="space-y-2">
          {checkInHint && (
            <p data-testid="player-check-in-hint" className="text-sm text-center py-2" style={{ color: '#A3A3A3' }}>{checkInHint}</p>
          )}
          {canSelfCheckIn && (
            <button data-testid="player-check-in" onClick={() => doAction('check-in')} disabled={actionLoading}
              className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-all"
              style={{ background: '#22C55E', color: '#fff', fontFamily: 'Oswald, sans-serif', opacity: actionLoading ? 0.6 : 1 }}>
              {actionLoading ? 'Checking in...' : "I'm Here — Check In"}
            </button>
          )}

          {player.status === 'active' && session.status === 'active' && (
            <>
              <button data-testid="player-pause" onClick={() => doAction('pause')} disabled={actionLoading}
                className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-all"
                style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid #F59E0B44', fontFamily: 'Oswald, sans-serif' }}>
                <Pause className="h-4 w-4 inline mr-2" />
                {actionLoading ? 'Pausing...' : "Pause — I'm Stepping Out"}
              </button>
              <p className="text-xs text-center" style={{ color: '#737373' }}>
                If your match is called while you're paused, someone takes your spot.
              </p>
            </>
          )}

          {player.status === 'playing' && session.status === 'active' && (
            player.pause_requested ? (
              <button data-testid="player-cancel-pause" onClick={() => doAction('resume')} disabled={actionLoading}
                className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-all"
                style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px solid #2A2A2A', fontFamily: 'Oswald, sans-serif' }}>
                {actionLoading ? 'Saving...' : 'Cancel pause — I’ll keep playing'}
              </button>
            ) : (
              <button data-testid="player-pause-after" onClick={() => doAction('pause')} disabled={actionLoading}
                className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-all"
                style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid #F59E0B44', fontFamily: 'Oswald, sans-serif' }}>
                <Pause className="h-4 w-4 inline mr-2" />
                {actionLoading ? 'Saving...' : 'Pause after this game'}
              </button>
            )
          )}

          {player.status === 'paused' && session.status === 'active' && (player.paused_by === 'owner' ? (
            <p data-testid="player-paused-by-owner" className="text-sm text-center py-2" style={{ color: '#F59E0B' }}>
              The coordinator paused you — please see the desk.
            </p>
          ) : (
            <button data-testid="player-resume" onClick={() => doAction('resume')} disabled={actionLoading}
              className="w-full py-3 rounded-xl text-sm font-bold uppercase tracking-wide transition-all"
              style={{ background: '#22C55E', color: '#fff', fontFamily: 'Oswald, sans-serif', opacity: actionLoading ? 0.6 : 1 }}>
              <Play className="h-4 w-4 inline mr-2" />
              {actionLoading ? 'Resuming...' : "I'm Back!"}
            </button>
          ))}
          {actionMsg && (
            <p data-testid="player-action-msg" role="status" className="text-xs text-center" style={{ color: '#A3A3A3' }}>{actionMsg}</p>
          )}
        </div>

        {/* Stats — always shown (0-state included) */}
        <div className="rounded-xl p-4" style={{ background: '#141414', border: '1px solid #262626' }}>
          <p className="text-xs uppercase font-bold mb-3" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>
            Today's Stats
          </p>
          <div className="grid grid-cols-4 gap-3 text-center">
            <div>
              <p className="text-xl font-bold" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>{player.matches_played || 0}</p>
              <p className="text-xs" style={{ color: '#737373' }}>Plays</p>
            </div>
            <div>
              <p className="text-xl font-bold" style={{ color: '#22C55E', fontFamily: 'Oswald, sans-serif' }}>{player.wins || 0}</p>
              <p className="text-xs" style={{ color: '#737373' }}>Wins</p>
            </div>
            <div>
              <p className="text-xl font-bold" style={{ color: '#EF4444', fontFamily: 'Oswald, sans-serif' }}>{player.losses || 0}</p>
              <p className="text-xs" style={{ color: '#737373' }}>Losses</p>
            </div>
            <div>
              <p className="text-xl font-bold" style={{ color: '#A3A3A3', fontFamily: 'Oswald, sans-serif' }}>
                {player.matches_played > 0 ? Math.round((player.wins / player.matches_played) * 100) : 0}%
              </p>
              <p className="text-xs" style={{ color: '#737373' }}>Win %</p>
            </div>
          </div>
          {data.avg_match_duration && (
            <p className="text-[11px] text-center mt-3" style={{ color: '#525252' }}>
              Avg match time: <b style={{ color: '#A3A3A3' }}>{data.avg_match_duration} min</b>
              {data.avg_is_live ? ' (live average)' : ' (estimated)'}
            </p>
          )}
        </div>

        {/* Payment status */}
        {player.amount_due > 0 && (
          <div className="rounded-xl p-4" data-testid="player-payment-card"
            style={{ background: '#141414', border: '1px solid #262626' }}>
            <p className="text-xs uppercase font-bold mb-2" style={{ color: '#737373', fontFamily: 'Oswald, sans-serif' }}>
              Payment
            </p>
            <div className={`grid ${data.waived > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-2 text-center`}>
              <div>
                <p className="text-[10px] uppercase" style={{ color: '#737373' }}>Due</p>
                <p className="text-sm font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>₱{Number(player.amount_due || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase" style={{ color: '#22C55E' }}>Paid</p>
                <p className="text-sm font-black" style={{ color: '#22C55E', fontFamily: 'Oswald, sans-serif' }}>₱{Number(player.amount_paid || 0).toLocaleString()}</p>
              </div>
              {data.waived > 0 && (
                <div data-testid="player-comped">
                  <p className="text-[10px] uppercase" style={{ color: '#A78BFA' }}>Comped</p>
                  <p className="text-sm font-black" style={{ color: '#A78BFA', fontFamily: 'Oswald, sans-serif' }}>₱{Number(data.waived).toLocaleString()}</p>
                </div>
              )}
              <div data-testid="player-balance">
                <p className="text-[10px] uppercase" style={{ color: data.balance > 0 ? '#E63946' : '#22C55E' }}>Balance</p>
                <p className="text-sm font-black" style={{ color: data.balance > 0 ? '#E63946' : '#22C55E', fontFamily: 'Oswald, sans-serif' }}>
                  {data.balance > 0 ? `₱${Number(data.balance).toLocaleString()}` : 'Settled'}
                </p>
              </div>
            </div>
            {player.payment_status && (
              <p className="text-[11px] text-center mt-2" style={{ color: '#A3A3A3' }}>
                Status: <b style={{ color: '#fff' }}>{player.payment_status.replace('_', ' ').toUpperCase()}</b>
              </p>
            )}
          </div>
        )}

        {/* Session info footer */}
        {session.status === 'ended' && (
          <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid #3B82F644' }}>
            <CheckCircle className="h-6 w-6 mx-auto mb-2" style={{ color: '#3B82F6' }} />
            <p className="text-sm font-bold" style={{ color: '#3B82F6' }}>Session Ended</p>
            {data.final?.rank ? (
              <p data-testid="player-final-rank" className="text-sm mt-1" style={{ color: '#fff' }}>
                You finished <b>#{data.final.rank}</b> of {data.final.of}
              </p>
            ) : (
              <p className="text-xs mt-1" style={{ color: '#737373' }}>
                {!data.final ? 'Thanks for playing! See you next time.'
                  : data.final.reason === 'record'
                    ? `You went ${data.final.wins}–${data.final.losses} — the podium needs more wins than losses, so you're not ranked tonight. Thanks for playing!`
                  : data.final.min_games && data.final.played > 0
                    ? `You played ${data.final.played} game${data.final.played === 1 ? '' : 's'} — the podium needs ${data.final.min_games}, so you're not ranked tonight. Thanks for playing!`
                    : 'No games recorded for you.'}
              </p>
            )}
            {standingsPath && (
              <a href={standingsPath} data-testid="player-standings-link" className="inline-block text-xs font-bold mt-2 underline"
                style={{ color: '#3B82F6' }}>View final standings</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
